let initialized = false;
let cachedConfig = null;
/**
 * True for the high-frequency, per-frame `[gap-diagnostics]` lines that
 * dominate log volume. Summaries (`*.summary`) and rare problem events
 * (notReady / resync / fifoDrop / LARGE JUMP) are always kept.
 */
function isHighVolumeDiagnostic(args) {
    if (args[0] !== '[gap-diagnostics]')
        return false;
    const payload = typeof args[1] === 'string' ? args[1] : '';
    if (payload.includes('.summary'))
        return false;
    if (/notReady|resync|fifoDrop|stallReset|LARGE JUMP/.test(payload))
        return false;
    return true;
}
/**
 * Read the iPhone debug harness URL params. Returns `null` when the
 * page is not running under the harness (i.e. no `remoteLog` query
 * param). The same config is later forwarded to every worker so that
 * `console.log` calls inside workers also reach the ingest server.
 */
export function getRemoteLogPageConfig() {
    if (cachedConfig !== null)
        return cachedConfig.endpoint || cachedConfig.lagMon
            ? cachedConfig
            : null;
    try {
        const params = new URLSearchParams(window.location.search);
        const endpoint = params.get('remoteLog') ?? '';
        const lagMon = params.get('lagMon') === '1' || params.get('lagMon') === 'true';
        // `lagMon=1` alone (no ingest endpoint) still yields a config so the
        // event-loop lag monitors can run with console-only capture (EC2
        // Playwright harness); the shipping part below is skipped.
        if (!endpoint && !lagMon)
            return null;
        cachedConfig = {
            endpoint,
            sessionId: params.get('debugSessionId') ?? undefined,
            summaryOnly: params.get('gapSummaryOnly') === 'true',
            lagMon,
        };
        return cachedConfig;
    }
    catch {
        return null;
    }
}
export function initRemoteLogger() {
    if (initialized)
        return;
    initialized = true;
    const cfg = getRemoteLogPageConfig();
    if (!cfg)
        return;
    // Opt-in only (`?lagMon=1`): both probes cost CPU on the phone — the
    // lag monitor is a 10ms self-scheduling timer — and must stay off
    // during CPU baseline runs. Independent of the ingest endpoint.
    if (cfg.lagMon) {
        installLongTaskObserver();
        installEventLoopLagMonitor();
    }
    if (!cfg.endpoint)
        return;
    const endpoint = cfg.endpoint;
    const sessionId = cfg.sessionId;
    const summaryOnly = cfg.summaryOnly === true;
    // NOTE: gap diagnostics are no longer auto-enabled by `remoteLog` —
    // they add per-frame work in the media hot paths, which perturbs CPU
    // baseline measurements taken through this harness. Pass
    // `?gapDiagnostics=true` explicitly when the probes are wanted
    // (isGapDiagnosticsEnabled() reads it directly on the main thread;
    // workers receive it via their start/init RPC parameters).
    const origLog = console.log;
    const origWarn = console.warn;
    const origError = console.error;
    const headers = {
        'Content-Type': 'application/json',
    };
    if (sessionId) {
        headers['X-Debug-Session-Id'] = sessionId;
    }
    // Batched shipping: one fetch per flush window instead of one per log
    // line. A per-line fetch() storm is itself a measurable CPU cost on the
    // phone, perturbing the measurements this harness exists to take. The
    // ingest server accepts a JSON array body.
    const FLUSH_INTERVAL_MS = 1000;
    const FLUSH_MAX_ENTRIES = 50;
    let queue = [];
    let flushTimer = null;
    const flush = (useKeepalive = false) => {
        if (queue.length === 0)
            return;
        const batch = queue;
        queue = [];
        if (flushTimer !== null) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        fetch(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(batch),
            // keepalive bodies count against a ~64KB quota — only use it
            // for the final pagehide flush, where losing it is the
            // alternative.
            keepalive: useKeepalive,
        }).catch(() => { });
    };
    const send = (level, args) => {
        const message = args
            .map((a) => {
            if (typeof a === 'string')
                return a;
            // Errors JSON.stringify to nearly nothing — keep the parts
            // that identify the failure.
            if (a instanceof Error) {
                const stackHead = a.stack
                    ?.split('\n')
                    .slice(0, 4)
                    .join(' | ');
                return `${a.name}: ${a.message}${stackHead ? ` [${stackHead}]` : ''}`;
            }
            try {
                return JSON.stringify(a);
            }
            catch {
                return String(a);
            }
        })
            .join(' ');
        const body = {
            level,
            message,
            timestamp: Date.now(),
            location: 'iPhone',
        };
        if (sessionId) {
            body.sessionId = sessionId;
        }
        queue.push(body);
        if (queue.length >= FLUSH_MAX_ENTRIES) {
            flush();
        }
        else if (flushTimer === null) {
            flushTimer = setTimeout(() => {
                flushTimer = null;
                flush();
            }, FLUSH_INTERVAL_MS);
        }
    };
    window.addEventListener('pagehide', () => flush(true));
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden')
            flush(true);
    });
    console.log = (...args) => {
        origLog.apply(console, args);
        if (summaryOnly && isHighVolumeDiagnostic(args))
            return;
        send('log', args);
    };
    console.warn = (...args) => {
        origWarn.apply(console, args);
        send('warn', args);
    };
    console.error = (...args) => {
        origError.apply(console, args);
        send('error', args);
    };
    window.addEventListener('error', (event) => {
        send('error', [
            `Uncaught ${event.error?.stack ?? event.message} at ${event.filename}:${event.lineno}`,
        ]);
    });
    window.addEventListener('unhandledrejection', (event) => {
        send('error', [
            `Unhandled rejection: ${event.reason?.stack ?? event.reason}`,
        ]);
    });
    origLog('[remoteLogger] Sending logs to', endpoint);
}
// #region agent log
// Main-thread event-loop occupancy monitor. iOS Safari does NOT support the
// `longtask` PerformanceObserver entry type (installLongTaskObserver no-ops
// there), so this self-scheduling timer is the only main-thread-block detector
// that works on iPhone. The iPhone `RTCPeerConnection`/SCTP transport lives on
// THIS thread (WebRTC isn't available in Workers on WebKit; only the
// datachannel is transferred to the node worker) — so if media arrives at the
// node worker in bursts while the worker is idle, main-thread occupancy here is
// the place to look for the cause. Mirrors the node-worker probe: continuous
// occupancy% per 1s window (so sub-block / distributed contention shows, not
// just hard >80ms blocks) plus individual `[loop-lag]` spikes at a 25ms
// threshold, to align with the media-gap timestamps.
function installEventLoopLagMonitor() {
    const INTERVAL = 10;
    const THRESH = 25;
    const WINDOW_MS = 1000;
    let last = typeof performance !== 'undefined' ? performance.now() : Date.now();
    let winStart = last;
    let winBusyMs = 0;
    let winMaxLag = 0;
    let winSpikes = 0;
    const tick = () => {
        const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
        const lag = now - last - INTERVAL;
        if (lag > 0)
            winBusyMs += lag;
        if (lag > winMaxLag)
            winMaxLag = lag;
        if (lag > THRESH) {
            winSpikes++;
            console.warn('[loop-lag]', JSON.stringify({
                where: 'main',
                lagMs: +lag.toFixed(1),
                ts: Date.now(),
            }));
        }
        last = now;
        const winElapsed = now - winStart;
        if (winElapsed >= WINDOW_MS) {
            console.warn('[loop-occupancy]', JSON.stringify({
                where: 'main',
                windowMs: +winElapsed.toFixed(0),
                busyMs: +winBusyMs.toFixed(1),
                occupancyPct: +((winBusyMs / winElapsed) * 100).toFixed(1),
                maxLagMs: +winMaxLag.toFixed(1),
                spikes: winSpikes,
                ts: Date.now(),
            }));
            winStart = now;
            winBusyMs = 0;
            winMaxLag = 0;
            winSpikes = 0;
        }
        setTimeout(tick, INTERVAL);
    };
    setTimeout(tick, INTERVAL);
}
// #endregion
/**
 * Main-thread `PerformanceObserver('longtask')` instrumentation. Every
 * long task ≥ 50 ms gets logged through the already-overridden
 * `console.log` (so it ships to the ingest endpoint). The iPhone
 * receive-chain diagnosis treats main-thread blocking as a primary
 * suspect for bursty `played` → `setReferenceTimes` delivery; this
 * probe surfaces those blocking windows directly.
 *
 * Safe to call even when the API is unavailable (older Safari, no
 * `longtask` entry type). The observer is process-wide, only the main
 * thread can install it.
 */
function installLongTaskObserver() {
    try {
        const Ctor = typeof PerformanceObserver !== 'undefined'
            ? PerformanceObserver
            : undefined;
        if (!Ctor)
            return;
        const supported = Ctor.supportedEntryTypes;
        if (supported && !supported.includes('longtask'))
            return;
        const observer = new Ctor((list) => {
            for (const entry of list.getEntries()) {
                console.log('[longtask]', JSON.stringify({
                    startTime: +entry.startTime.toFixed(2),
                    durationMs: +entry.duration.toFixed(2),
                    name: entry.name,
                }));
            }
        });
        observer.observe({ entryTypes: ['longtask'] });
    }
    catch (e) {
        console.warn('[remoteLogger] longtask observer failed', e);
    }
}
